import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { usePathname } from 'expo-router';
import { useQueryClient } from '@tanstack/react-query';
import type {
  SubscriptionSyncInput,
  SubscriptionWebCheckoutInput,
} from '@tabbio/types';
import { useAuth } from '../hooks/useAuth';
import { useWorkspace } from './useWorkspace';
import {
  fetchSubscriptionManagementUrl,
  fetchSubscriptionWebCheckoutLink,
  isExpectedSubscriptionSyncError,
  syncSubscriptionState,
} from '../lib/subscription-api';
import { subscriptionStateMeetsPurchaseTier } from '../lib/subscription-purchase';
import { AppAuthRequiredError, isAppAuthRequiredError } from '../lib/app-auth';
import {
  captureProductException,
  getErrorObservabilityProperties,
  trackProductEvent,
} from '../lib/product-analytics';
import { scheduleAfterFirstPaint } from '../lib/schedule-after-first-paint';
import { RevenueCatContext } from './RevenueCatProvider.shared';

const AUTOMATIC_SUBSCRIPTION_SYNC_BACKOFF_MS = 10 * 60 * 1000;
type SubscriptionSyncSource = 'automatic' | 'manual';

function isBackgroundSubscriptionSync(source: SubscriptionSyncSource) {
  return source === 'automatic';
}

function isAuthSurface(pathname: string | null) {
  return Boolean(
    pathname === '/login' ||
    pathname === '/auth-complete' ||
    pathname === '/resume-import' ||
    pathname?.startsWith('/login/') ||
    pathname?.startsWith('/auth-complete/') ||
    pathname?.startsWith('/resume-import/'),
  );
}

function isAuthenticationRequiredError(error: unknown) {
  if (isAppAuthRequiredError(error)) {
    return true;
  }

  const message = error instanceof Error ? error.message.toLowerCase() : '';
  return (
    message.includes('authentication required') ||
    message.includes('sign in to manage your subscription')
  );
}

export function RevenueCatProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const pathname = usePathname();
  const { user, isAuthenticated, isOnboardingCompleted, isTokenReady } =
    useAuth();
  const { activeWorkspaceId, mode, companyId } = useWorkspace();
  const [syncEnabledAfterPaint, setSyncEnabledAfterPaint] = useState(false);
  const pathnameRef = useRef(pathname);
  const autoSyncPausedUntilRef = useRef(0);

  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);

  useEffect(() => {
    if (!isAuthenticated || !isTokenReady) {
      setSyncEnabledAfterPaint(false);
      return;
    }

    return scheduleAfterFirstPaint(() => {
      setSyncEnabledAfterPaint(true);
    });
  }, [isAuthenticated, isTokenReady, activeWorkspaceId, mode]);

  const getDefaultSyncInput = useCallback(
    (): SubscriptionSyncInput => ({
      companyId: companyId ?? null,
      planSet: mode === 'employer' ? 'employer' : 'individual',
    }),
    [companyId, mode],
  );

  const invalidateSubscriptionQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['subscription'] });
  }, [queryClient]);

  const shouldRunAutomaticSync = useCallback(
    () => Date.now() >= autoSyncPausedUntilRef.current,
    [],
  );

  const getRelevantSyncInputs = useCallback(
    (input?: SubscriptionSyncInput) => {
      const primary = input ?? getDefaultSyncInput();
      const employerCompanyId =
        primary.planSet === 'employer'
          ? primary.companyId ?? companyId ?? null
          : primary.companyId ?? companyId ?? null;
      const targets: SubscriptionSyncInput[] = [];
      const seen = new Set<string>();

      const pushTarget = (
        planSet: 'individual' | 'employer',
        targetCompanyId?: string | null,
        options?: { allowMissingCompanyId?: boolean },
      ) => {
        if (planSet === 'employer' && !targetCompanyId && !options?.allowMissingCompanyId) {
          return;
        }

        const normalized: SubscriptionSyncInput =
          planSet === 'employer'
            ? { planSet: 'employer', companyId: targetCompanyId ?? null }
            : { planSet: 'individual', companyId: null };
        const key = `${normalized.planSet}:${normalized.companyId ?? 'none'}`;
        if (seen.has(key)) {
          return;
        }

        seen.add(key);
        targets.push(normalized);
      };

      pushTarget(primary.planSet ?? 'individual', primary.companyId ?? null, {
        allowMissingCompanyId: true,
      });
      pushTarget('individual', null);
      pushTarget('employer', employerCompanyId);

      return targets;
    },
    [companyId, getDefaultSyncInput],
  );

  const requireAuthenticatedUser = useCallback(() => {
    if (!isAuthenticated || !isTokenReady || !user?.id) {
      throw new AppAuthRequiredError('Sign in to manage your subscription');
    }
  }, [isAuthenticated, isTokenReady, user?.id]);

  const syncSubscription = useCallback(
    async (
      input?: SubscriptionSyncInput,
      options?: { source?: SubscriptionSyncSource },
    ) => {
      requireAuthenticatedUser();
      const syncInputs = getRelevantSyncInputs(input);
      try {
        let result = null;
        for (const [index, nextInput] of syncInputs.entries()) {
          const nextResult = await syncSubscriptionState(nextInput);
          if (index === 0) {
            result = nextResult;
          }
        }
        invalidateSubscriptionQueries();
        trackProductEvent('subscription_sync_completed', {
          planSet: input?.planSet ?? getDefaultSyncInput().planSet,
          hasCompanyId: Boolean(input?.companyId ?? companyId),
          targetCount: syncInputs.length,
          surface: 'web',
        });
        return result;
      } catch (error) {
        const nextError = error instanceof Error ? error : new Error('Subscription sync failed');
        const isAuthRequired = isAuthenticationRequiredError(nextError);
        const isExpectedSyncError = isExpectedSubscriptionSyncError(nextError);
        const source = options?.source ?? 'manual';
        const isBackgroundSync = isBackgroundSubscriptionSync(source);
        const route = pathnameRef.current;
        const suppressUnauthenticatedSyncReport = isAuthSurface(route);
        trackProductEvent('subscription_sync_failed', {
          planSet: input?.planSet ?? getDefaultSyncInput().planSet,
          hasCompanyId: Boolean(input?.companyId ?? companyId),
          targetCount: syncInputs.length,
          surface: 'web',
          source,
          route,
          ...getErrorObservabilityProperties(nextError),
        });
        if (isExpectedSyncError || isAuthRequired) {
          autoSyncPausedUntilRef.current = Date.now() + AUTOMATIC_SUBSCRIPTION_SYNC_BACKOFF_MS;
        }

        if (
          !isAuthRequired &&
          !(isBackgroundSync && isExpectedSyncError) &&
          (!suppressUnauthenticatedSyncReport ||
            !isAuthenticationRequiredError(nextError))
        ) {
          captureProductException(nextError, {
            feature: 'subscription',
            action: 'sync',
            planSet: input?.planSet ?? getDefaultSyncInput().planSet,
            surface: 'web',
            source,
            route,
          });
        }
        throw nextError;
      }
    },
    [
      companyId,
      getDefaultSyncInput,
      getRelevantSyncInputs,
      invalidateSubscriptionQueries,
      requireAuthenticatedUser,
    ],
  );

  const restorePurchases = useCallback(
    async (input?: SubscriptionSyncInput) => {
      const target = input ?? getDefaultSyncInput();
      trackProductEvent('subscription_restore_started', {
        planSet: target.planSet,
        hasCompanyId: Boolean(target.companyId),
        surface: 'web',
      });
      try {
        const result = await syncSubscription(input);
        trackProductEvent('subscription_restore_completed', {
          planSet: target.planSet,
          hasCompanyId: Boolean(target.companyId),
          surface: 'web',
        });
        return result;
      } catch (error) {
        trackProductEvent('subscription_restore_failed', {
          planSet: target.planSet,
          hasCompanyId: Boolean(target.companyId),
          surface: 'web',
          ...getErrorObservabilityProperties(error),
        });
        throw error;
      }
    },
    [getDefaultSyncInput, syncSubscription],
  );

  const openManagementUrl = useCallback(
    async (targetCompanyId?: string | null) => {
      requireAuthenticatedUser();
      const resolvedCompanyId =
        typeof targetCompanyId === 'undefined' ? companyId ?? null : targetCompanyId;
      trackProductEvent('subscription_management_open_started', {
        hasCompanyId: Boolean(resolvedCompanyId),
        surface: 'web',
      });
      try {
        const result = await fetchSubscriptionManagementUrl(resolvedCompanyId);
        if (!result.managementUrl) {
          throw new Error('No billing management URL is available');
        }
        if (typeof window !== 'undefined') {
          window.location.assign(result.managementUrl);
        }
        trackProductEvent('subscription_management_opened', {
          hasCompanyId: Boolean(resolvedCompanyId),
          store: result.store ?? 'unknown',
          source: result.source ?? 'unknown',
          surface: 'web',
        });
        return result;
      } catch (error) {
        const nextError = error instanceof Error ? error : new Error('Unable to open billing management');
        trackProductEvent('subscription_management_open_failed', {
          hasCompanyId: Boolean(resolvedCompanyId),
          surface: 'web',
          ...getErrorObservabilityProperties(nextError),
        });
        if (!isAuthenticationRequiredError(nextError)) {
          captureProductException(nextError, {
            feature: 'subscription',
            action: 'open_management',
            surface: 'web',
          });
        }
        throw nextError;
      }
    },
    [companyId, requireAuthenticatedUser],
  );

  const startPurchase = useCallback(
    async (input: SubscriptionWebCheckoutInput): Promise<'purchased' | 'checkout'> => {
      requireAuthenticatedUser();
      trackProductEvent('subscription_checkout_started', {
        planSet: input.planSet,
        tier: input.tier,
        billingInterval: input.billingInterval,
        hasCompanyId: Boolean(input.companyId),
        surface: 'web',
      });

      try {
        const syncedState = await syncSubscription({
          planSet: input.planSet,
          companyId: input.companyId ?? null,
        });
        if (subscriptionStateMeetsPurchaseTier(syncedState, input)) {
          trackProductEvent('subscription_purchase_completed', {
            planSet: input.planSet,
            tier: input.tier,
            billingInterval: input.billingInterval,
            hasCompanyId: Boolean(input.companyId),
            surface: 'web',
          });
          return 'purchased';
        }

        const checkout = await fetchSubscriptionWebCheckoutLink({
          ...input,
          surface: 'web',
        });

        trackProductEvent('subscription_checkout_link_created', {
          planSet: input.planSet,
          tier: input.tier,
          billingInterval: input.billingInterval,
          hasCompanyId: Boolean(input.companyId),
          quantity: checkout.quantity,
          surface: 'web',
        });

        if (typeof window !== 'undefined') {
          window.location.assign(checkout.checkoutUrl);
        }

        return 'checkout';
      } catch (error) {
        const nextError = error instanceof Error ? error : new Error('Unable to start checkout');
        trackProductEvent('subscription_checkout_failed', {
          planSet: input.planSet,
          tier: input.tier,
          billingInterval: input.billingInterval,
          hasCompanyId: Boolean(input.companyId),
          surface: 'web',
          ...getErrorObservabilityProperties(nextError),
        });
        if (!isAuthenticationRequiredError(nextError)) {
          captureProductException(nextError, {
            feature: 'subscription',
            action: 'checkout',
            planSet: input.planSet,
            tier: input.tier,
            billingInterval: input.billingInterval,
            surface: 'web',
          });
        }
        throw nextError;
      }
    },
    [requireAuthenticatedUser, syncSubscription],
  );

  useEffect(() => {
    if (
      !syncEnabledAfterPaint ||
      !isAuthenticated ||
      !isTokenReady ||
      !isOnboardingCompleted ||
      !shouldRunAutomaticSync()
    ) {
      return;
    }

    void syncSubscription(getDefaultSyncInput(), { source: 'automatic' }).catch(() => {
      // The subscription provider will still refetch when the screen changes.
    });
  }, [
    getDefaultSyncInput,
    isAuthenticated,
    isTokenReady,
    isOnboardingCompleted,
    shouldRunAutomaticSync,
    syncEnabledAfterPaint,
    syncSubscription,
  ]);

  useEffect(() => {
    if (!syncEnabledAfterPaint || typeof document === 'undefined' || typeof window === 'undefined') {
      return;
    }

    const handleVisibility = () => {
      if (
        document.visibilityState === 'visible' &&
        isAuthenticated &&
        isTokenReady &&
        isOnboardingCompleted &&
        shouldRunAutomaticSync()
      ) {
        void syncSubscription(getDefaultSyncInput(), {
          source: 'automatic',
        }).catch(() => {
          // Ignore background refresh failures.
        });
      }
    };

    window.addEventListener('focus', handleVisibility);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('focus', handleVisibility);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [
    getDefaultSyncInput,
    isAuthenticated,
    isTokenReady,
    isOnboardingCompleted,
    shouldRunAutomaticSync,
    syncEnabledAfterPaint,
    syncSubscription,
  ]);

  const value = useMemo(
    () => ({
      isReady: true,
      isBusy: false,
      lastError: null,
      syncSubscription,
      startPurchase,
      restorePurchases,
      openManagementUrl,
    }),
    [openManagementUrl, restorePurchases, startPurchase, syncSubscription],
  );

  return <RevenueCatContext.Provider value={value}>{children}</RevenueCatContext.Provider>;
}
